import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  LocationGroup,
  LocationGroupSection,
  LocationPickerHeader,
  useLocationPicker,
  LocationSearchGroup,
  LocationSearchResults,
  GroupSearchResults,
  OrgSearchResults,
  useLocationPickerStore,
  LocationGroupSectionSimpleView,
} from '@frontend/location-picker';
import { useLocationGroupStore } from '@frontend/scope';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Button, Text, emptyStateGraphics, Truncated } from '@frontend/design-system';
import { UnprivilegedPickerProvider, useUnprivilegedPicker } from './unprivileged-picker-context';

export interface LocationGroupResult {
  groupId: string;
  groupName: string;
  orgId: string;
  orgName: string;
  locations: Array<{
    id: string;
    name: string;
  }>;
}

interface UnprivilegedLocationPickerProps {
  scopeHierarchy: Record<string, Record<string, string[]>>;
  getScopeName: (scope: string, type?: 'location' | 'group' | 'org') => string;
  selectedLocationIds: string[];
  selectedGroupId: string;
  selectedOrgId: string;
  closeModal?: () => void;
  children: React.ReactNode;
  renderOrgInfo?: (orgId: string) => JSX.Element | null;
  renderLocationInfo?: (locationId: string) => JSX.Element | null;
  renderBannerInfo?: () => JSX.Element | null;
  onApplyError?: (error: unknown) => void;
  onApply: (selectedOrgId: string, selectedGroupId: string, selectedLocationIds: string[]) => Promise<void>;
  onClickOrgRadio?: () => void;
  isSimpleView?: boolean;
}

export const UnprivilegedLocationProvider = ({
  getScopeName,
  scopeHierarchy,
  selectedLocationIds,
  selectedGroupId,
  selectedOrgId,
  closeModal,
  children,
  renderBannerInfo,
  renderLocationInfo,
  renderOrgInfo,
  onApplyError,
  onApply,
  onClickOrgRadio,
  isSimpleView,
}: UnprivilegedLocationPickerProps) => {
  const locationPickerStore = useLocationPickerStore();
  useEffect(() => {
    if (!onClickOrgRadio) return;

    const unsubscribe = locationPickerStore.subscribe(
      (state) => state.inFlightOrganizationId,
      (_) => {
        onClickOrgRadio();
      }
    );

    return () => {
      unsubscribe();
    };
  }, [onClickOrgRadio]);

  return (
    <UnprivilegedPickerProvider
      scopeHierarchy={scopeHierarchy}
      getScopeName={getScopeName}
      selectedOrgId={selectedOrgId}
      selectedGroupId={selectedGroupId}
      selectedLocationIds={selectedLocationIds}
      closeModal={closeModal}
      renderBannerInfo={renderBannerInfo}
      renderLocationInfo={renderLocationInfo}
      renderOrgInfo={renderOrgInfo}
      onApplyError={onApplyError}
      onApply={onApply}
      isSimpleView={isSimpleView}
    >
      {children}
    </UnprivilegedPickerProvider>
  );
};

const LocationPickerFooter = () => {
  const locationPickerStore = useLocationPickerStore();
  const { inFlightOrganizationId, inFlightGroupId, inFlightLocationIds, organizationViewId } = useLocationPicker([
    'inFlightOrganizationId',
    'inFlightGroupId',
    'inFlightLocationIds',
    'organizationViewId',
  ]);
  const [filterLoading, setFilterLoading] = useState(false);
  const closeModal = useUnprivilegedPicker((state) => state.closeModal);
  const onApplyError = useUnprivilegedPicker((state) => state.onApplyError);
  const onApply = useUnprivilegedPicker((state) => state.onApply);
  const hasSwitchedScope = useUnprivilegedPicker((state) => state.hasSwitchedScope);
  const hasAccessToGroupOnly = useUnprivilegedPicker((state) => state.hasAccessToGroupOnly);
  const hasInFlightEmptyGroup = useUnprivilegedPicker((state) => state.hasInFlightEmptyGroup);
  const isSimpleView = useUnprivilegedPicker((state) => state.isSimpleView);
  const scopeHierarchy = useUnprivilegedPicker((state) => state.scopeHierarchy);
  const totalNumOfLocations = useLocationGroupStore.getState().accessibleLocationIds.length;
  const { t } = useTranslation('location-picker');

  const isApplyDisabled = () => {
    const groupOnly = hasAccessToGroupOnly();

    if (groupOnly === 'already-selected-empty-group') return true;

    if (groupOnly === 'available-empty-group' || hasInFlightEmptyGroup) return false;

    return !hasSwitchedScope || !inFlightOrganizationId || !inFlightGroupId || inFlightLocationIds.length === 0;
  };

  const handleApplySelection = async () => {
    try {
      setFilterLoading(true);
      const { inFlightOrganizationId, inFlightGroupId, inFlightLocationIds } = locationPickerStore.getState();
      pendo.track('location_selector_apply_click', {
        selectedLocationCount: inFlightLocationIds.length,
        totalLocationsAvailable: totalNumOfLocations,
      });
      const getLocationIds = () => {
        if (hasAccessToGroupOnly() === 'available-empty-group') {
          return Object.keys(scopeHierarchy[organizationViewId] || {});
        }

        if (hasInFlightEmptyGroup) {
          return [inFlightGroupId];
        }

        return inFlightLocationIds;
      };

      const organizationId =
        hasAccessToGroupOnly() === 'available-empty-group' ? organizationViewId : inFlightOrganizationId;

      await onApply(organizationId, inFlightGroupId, getLocationIds());

      closeModal?.();
    } catch (error) {
      onApplyError ? onApplyError(error) : console.error(error);
    } finally {
      setFilterLoading(false);
    }
  };

  return (
    <div
      css={css`
        padding: ${theme.spacing(2)};
        gap: ${theme.spacing(2)};
        border-top: 1px solid ${theme.colors.neutral20};
      `}
    >
      <InFlightSelection />
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          gap: ${theme.spacing(2)};
        `}
      >
        {!isSimpleView && (
          <Button variant='secondary' size='large' onClick={closeModal}>
            {t('Cancel')}
          </Button>
        )}
        <Button disabled={isApplyDisabled()} loading={filterLoading} size='large' onClick={handleApplySelection}>
          {t('Apply')}
        </Button>
      </div>
    </div>
  );
};

export const UnprivilegedLocationPicker = () => {
  const isSimpleView = useUnprivilegedPicker((state) => state.isSimpleView);

  return (
    <>
      {!isSimpleView && <CurrentlyAppliedScopeWrapper />}
      <LocationPickerHeaderWrapper />
      <LocationPickerBody />
      <LocationPickerBanners />
      <LocationPickerFooter />
    </>
  );
};

export const LocationPickerBanners = () => {
  const renderBannerInfo = useUnprivilegedPicker((state) => state.renderBannerInfo);

  return renderBannerInfo?.();
};

const CurrentlyAppliedScopeWrapper = () => {
  const selectedLocationIds = useUnprivilegedPicker((state) => state.selectedLocationIds);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const selectedOrgId = useUnprivilegedPicker((state) => state.selectedOrgId);
  const selectedGroupId = useUnprivilegedPicker((state) => state.selectedGroupId);
  const locationNames = useUnprivilegedPicker((state) => state.getLocationNames(selectedLocationIds));

  return (
    <CurrentlyAppliedScope
      organizationName={getScopeName(selectedOrgId, 'org')}
      groupName={getScopeName(selectedGroupId, 'group')}
      locationName={locationNames}
    />
  );
};

const LocationPickerHeaderWrapper = () => {
  const searchResultCount = useUnprivilegedPicker((state) => state.searchResultCount);
  const orgIds = useUnprivilegedPicker((state) => state.orgIds);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const renderOrgInfo = useUnprivilegedPicker((state) => state.renderOrgInfo);

  return (
    <LocationPickerHeader
      count={searchResultCount}
      organizationIds={orgIds}
      getScopeName={getScopeName}
      renderOrgInfo={renderOrgInfo}
    />
  );
};

const containerStyle = (alignText: 'left' | 'right' = 'left') => css`
  overflow: hidden;
  text-align: ${alignText};
`;

const breadcrumbsContainerStyle = (alignText: 'left' | 'right' = 'left') => [
  css`
    display: flex;
    align-items: center;
    width: 100%;
    gap: ${theme.spacing(1)};
    max-width: 400px;
    margin-bottom: ${theme.spacing(3)};
  `,
  alignText === 'right'
    ? css`
        justify-content: flex-end;
        margin-left: auto;
      `
    : css`
        justify-content: flex-start;
      `,
];

export const InFlightSelection = () => {
  const { t } = useTranslation('location-picker');
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const hasAccessToGroupOnly = useUnprivilegedPicker((state) => state.hasAccessToGroupOnly);

  const { inFlightOrganizationId, inFlightGroupId, inFlightLocationIds, organizationViewId } = useLocationPicker([
    'inFlightOrganizationId',
    'inFlightGroupId',
    'inFlightLocationIds',
    'organizationViewId',
  ]);

  const hasSwitchedScope = useUnprivilegedPicker((state) => state.hasSwitchedScope);
  const scopeHierarchy = useUnprivilegedPicker((state) => state.scopeHierarchy);

  const locationNames = useUnprivilegedPicker((state) => state.getLocationNames(inFlightLocationIds));

  const hasInFlightEmptyGroup = useUnprivilegedPicker((state) => state.hasInFlightEmptyGroup);

  const shouldDisplayScopeChangeIndicator = () => {
    /*This check is to ensure that we don't show the "Switching to" section
     when the user is already in the group-only access view or if they selected a group
     that has no locations.
    */
    if (hasAccessToGroupOnly() === 'already-selected-empty-group') {
      return false;
    }

    if (hasAccessToGroupOnly() === 'available-empty-group' || hasInFlightEmptyGroup) {
      return true;
    }

    return hasSwitchedScope;
  };

  const displayScopeChangeIndicatorForGroupOnly = () => {
    return hasInFlightEmptyGroup || hasAccessToGroupOnly() === 'available-empty-group';
  };

  const displayScopeChangeIndicatorForGroupOnlyText = () => {
    if (hasInFlightEmptyGroup) {
      return `${getScopeName(inFlightGroupId, 'group')}`;
    }

    const orgOnlyGroup = Object.keys(scopeHierarchy[organizationViewId])[0];
    const orgOnlyGroupName = getScopeName(orgOnlyGroup, 'group');

    return orgOnlyGroupName;
  };

  return (
    <AnimatePresence>
      {shouldDisplayScopeChangeIndicator() ? (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          exit={{ height: 0 }}
          key={`${displayScopeChangeIndicatorForGroupOnly()}`}
          css={containerStyle('right')}
        >
          {displayScopeChangeIndicatorForGroupOnly() ? (
            <div css={breadcrumbsContainerStyle('right')}>
              <Text size='large' weight='light' color='subdued'>
                <Text weight='light' color='subdued'>
                  {t('Switching to: ')}
                </Text>
                <Text as='span' weight='bold' color='default'>
                  {displayScopeChangeIndicatorForGroupOnlyText()}
                </Text>
                <Text color='subdued' size='small'>
                  {t(`This location group contains locations that you are not authorized to access. To manage your
                  organization's settings, click 'Apply'.`)}
                </Text>
              </Text>
            </div>
          ) : (
            <LocationBreadcrumbDisplay
              label={t('Switching to:')}
              organizationName={getScopeName(inFlightOrganizationId, 'org')}
              groupName={getScopeName(inFlightGroupId)}
              locationName={locationNames}
              alignText='right'
            />
          )}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

const LocationBreadcrumbDisplay = ({
  label,
  organizationName,
  groupName,
  locationName,
  alignText = 'left',
}: {
  label: string;
  organizationName: string;
  groupName: string;
  locationName: string;
  alignText?: 'left' | 'right';
}) => {
  const { hasSingleOrganization, hasSingleLocationGroup } = useLocationPicker([
    'hasSingleOrganization',
    'hasSingleLocationGroup',
  ]);

  return (
    <>
      <Text size='large' color='subdued'>
        {label}
      </Text>
      <div css={breadcrumbsContainerStyle(alignText)}>
        {!hasSingleOrganization && (
          <>
            <LocationBreadcrumb>{organizationName || '...'}</LocationBreadcrumb>
            <Icon name='caret-right-small' />
          </>
        )}
        {!hasSingleLocationGroup && (
          <>
            <LocationBreadcrumb>{groupName || '...'}</LocationBreadcrumb>
            <Icon name='caret-right-small' />
          </>
        )}
        <LocationBreadcrumb>{locationName || '...'}</LocationBreadcrumb>
      </div>
    </>
  );
};

const CurrentlyAppliedScope = ({
  organizationName,
  groupName,
  locationName,
}: {
  organizationName: string;
  groupName: string;
  locationName: string;
}) => {
  const { t } = useTranslation('location-picker');

  if (!organizationName && !groupName && !locationName) {
    return null;
  }

  return (
    <div
      css={[
        containerStyle(),
        css`
          margin-left: ${theme.spacing(3)};
        `,
      ]}
    >
      <LocationBreadcrumbDisplay
        label={t('Currently Applied:')}
        organizationName={organizationName}
        groupName={groupName}
        locationName={locationName}
      />
    </div>
  );
};

InFlightSelection.displayName = 'InFlightSelection';

const breadcrumbStyle = css`
  color: ${theme.font.colors.default};
  flex: 0 1 auto !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline;
  text-align: start;
  width: auto;
`;

const LocationBreadcrumb = ({ children }: { children: React.ReactNode }) => (
  <Truncated as={Text} size='large' css={breadcrumbStyle}>
    {children}
  </Truncated>
);

const EmptyState = emptyStateGraphics.search;

const EmptyStateGraphic = () => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: ${theme.spacing(3, 5)};
      `}
    >
      <EmptyState
        css={css`
          max-width: 300px;
          max-height: 300px;
        `}
      />
      <Text css={{ width: '65%' }} textAlign='center' color='light' size='large'>
        Start searching Organizations, Location Groups, or Locations using the search bar above.
      </Text>
    </div>
  );
};

const LocationPickerBody = () => {
  const { isSearchMode } = useLocationPicker(['isSearchMode']);

  return (
    <div style={{ overflow: 'auto', flex: 1 }}>
      {!isSearchMode ? <LocationGroupsDisplay /> : <SearchResultsDisplay />}
    </div>
  );
};

const SearchResultsDisplay = () => {
  const { setInFlightLocationIds } = useLocationPicker(['setInFlightLocationIds']);
  const searchResults = useUnprivilegedPicker((state) => state.searchResults);
  const orgLocationGroups = useUnprivilegedPicker((state) => state.orgLocationGroups);
  const scopeHierarchy = useUnprivilegedPicker((state) => state.scopeHierarchy);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const hasSearchResults = useUnprivilegedPicker((state) => state.hasSearchResults);
  const renderLocationInfo = useUnprivilegedPicker((state) => state.renderLocationInfo);

  if (!hasSearchResults) {
    return <EmptyStateGraphic />;
  }

  return (
    <>
      {searchResults.locationGroups.length > 0 && (
        <LocationSearchGroup id='locations'>
          {searchResults.locationGroups.map((group) => (
            <LocationSearchResults renderLocationInfo={renderLocationInfo} key={group.groupId} groupResults={group} />
          ))}
        </LocationSearchGroup>
      )}

      {searchResults.groups.length > 0 && (
        <LocationSearchGroup id='locationGroups'>
          {searchResults.groups.map((group) => (
            <GroupSearchResults
              key={group.id}
              organizationName={group.orgName}
              organizationId={group.orgId}
              locationGroupId={group.id}
              locationGroupName={group.name}
              numberOfLocations={group.locationCount || 0}
              locations={scopeHierarchy[group.orgId]?.[group.id] || []}
              getScopeName={getScopeName}
              onSelected={() => {
                setInFlightLocationIds(scopeHierarchy[group.orgId][group.id]);
              }}
            />
          ))}
        </LocationSearchGroup>
      )}

      {searchResults.organizations.length > 0 && (
        <LocationSearchGroup id='organizations'>
          {searchResults.organizations.map((org) => (
            <OrgSearchResults
              key={org.id}
              organizationName={org.name}
              organizationId={org.id}
              numberOfGroups={org.groupCount || 0}
              numberOfLocations={org.locationCount || 0}
              locationGroups={orgLocationGroups[org.id] || []}
              getScopeName={getScopeName}
            />
          ))}
        </LocationSearchGroup>
      )}
    </>
  );
};

const LocationGroupsDisplay = () => {
  const { organizationViewId } = useLocationPicker(['organizationViewId']);

  const currentOrgLocationGroups = useUnprivilegedPicker((state) => state.currentOrgLocationGroups);
  const currentOrgData = useUnprivilegedPicker((state) => state.currentOrgData);
  const getScopeName = useUnprivilegedPicker((state) => state.getScopeName);
  const renderLocationInfo = useUnprivilegedPicker((state) => state.renderLocationInfo);

  return currentOrgLocationGroups.length > 1 ? (
    <LocationGroup key={organizationViewId}>
      {currentOrgLocationGroups.map((locationGroup) => (
        <LocationGroupSection
          key={locationGroup}
          groupId={locationGroup}
          groupLocations={currentOrgData[locationGroup] || []}
          getScopeName={getScopeName}
          renderLocationInfo={renderLocationInfo}
        >
          {getScopeName(locationGroup, 'group')}
        </LocationGroupSection>
      ))}
    </LocationGroup>
  ) : (
    <div
      css={css`
        padding: ${theme.spacing(1, 2)};
      `}
    >
      <LocationGroupSectionSimpleView
        groupId={currentOrgLocationGroups[0]}
        groupLocations={currentOrgData[currentOrgLocationGroups[0]] || []}
        getScopeName={getScopeName}
        renderLocationInfo={renderLocationInfo}
      />
    </div>
  );
};
